import { useState, useEffect, useCallback, useRef } from 'react'
import { fetchScoreboard } from '@/lib/api'
import type { ScoreboardDay, ScoreboardGame } from '@/types/api'

export interface UseScoreboardResult {
  days: ScoreboardDay[]
  featured: ScoreboardGame[]
  loadingInitial: boolean
  loadingMore: boolean
  initialError: boolean
  moreError: boolean
  hasMore: boolean
  loadMore: () => void
  loadUntil: (targetDate: string) => Promise<void>
  retryInitial: () => void
  refetchAnchorDay: () => void
}

/**
 * Drives the homepage scoreboard: loads the anchor day first (today, or last
 * night in the morning) plus the featured strip, then pages backward one
 * populated day at a time (empty days are skipped server-side).
 *
 * - `loadUntil` keeps paging until a given date is loaded (or passed), for the
 *   quick-jump date strip.
 * - `refetchAnchorDay` refreshes only the first section for live polling without
 *   disturbing already-loaded history.
 */
export function useScoreboard(sport: string): UseScoreboardResult {
  const [days, setDays] = useState<ScoreboardDay[]>([])
  const [featured, setFeatured] = useState<ScoreboardGame[]>([])
  const [loadingInitial, setLoadingInitial] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [initialError, setInitialError] = useState(false)
  const [moreError, setMoreError] = useState(false)
  const [hasMore, setHasMore] = useState(true)

  const mountedRef = useRef(true)
  // Guards against overlapping loadMore / loadUntil (rapid scroll, observer refire).
  const loadingMoreRef = useRef(false)
  // Mirrors `days` so loops can read the latest cursor without stale closures.
  const daysRef = useRef<ScoreboardDay[]>([])
  daysRef.current = days

  const loadInitial = useCallback(async () => {
    setLoadingInitial(true)
    setInitialError(false)
    setMoreError(false)
    setHasMore(true)
    setDays([])
    setFeatured([])
    try {
      const day = await fetchScoreboard(sport)
      if (!mountedRef.current) return
      setDays([day])
      setFeatured(day.featured ?? [])
      setHasMore(day.next_cursor != null)
    } catch {
      if (mountedRef.current) setInitialError(true)
    } finally {
      if (mountedRef.current) setLoadingInitial(false)
    }
  }, [sport])

  useEffect(() => {
    mountedRef.current = true
    loadInitial()
    return () => { mountedRef.current = false }
  }, [loadInitial])

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current) return
    const cursor = daysRef.current[daysRef.current.length - 1]?.next_cursor
    if (!cursor) return

    loadingMoreRef.current = true
    setLoadingMore(true)
    setMoreError(false)
    try {
      const day = await fetchScoreboard(sport, cursor)
      if (!mountedRef.current) return
      if (day.date == null) {
        setHasMore(false) // no more history
      } else {
        setDays(prev => [...prev, day])
        setHasMore(day.next_cursor != null)
      }
    } catch {
      if (mountedRef.current) setMoreError(true)
    } finally {
      loadingMoreRef.current = false
      if (mountedRef.current) setLoadingMore(false)
    }
  }, [sport])

  // Page backward until a day on/older than `targetDate` is loaded (days are
  // strictly descending, so this reaches the target or the nearest older
  // populated day when the target itself has no games). No-op if already covered.
  const loadUntil = useCallback(async (targetDate: string) => {
    if (loadingMoreRef.current) return
    const oldest = daysRef.current[daysRef.current.length - 1]
    if (oldest?.date && oldest.date <= targetDate) return // already loaded/past
    let cursor = oldest?.next_cursor
    if (!cursor) return

    loadingMoreRef.current = true
    setLoadingMore(true)
    setMoreError(false)
    try {
      while (cursor) {
        const day = await fetchScoreboard(sport, cursor)
        if (!mountedRef.current) return
        if (day.date == null) { setHasMore(false); break }
        setDays(prev => [...prev, day])
        setHasMore(day.next_cursor != null)
        if (day.date <= targetDate) break
        cursor = day.next_cursor
      }
    } catch {
      if (mountedRef.current) setMoreError(true)
    } finally {
      loadingMoreRef.current = false
      if (mountedRef.current) setLoadingMore(false)
    }
  }, [sport])

  // Refresh only the anchor (first) section for live polling. Leaves history
  // untouched and silently keeps the current data on a transient failure.
  const refetchAnchorDay = useCallback(async () => {
    try {
      const day = await fetchScoreboard(sport)
      if (!mountedRef.current) return
      setDays(prev => (prev.length > 0 ? [day, ...prev.slice(1)] : [day]))
      setFeatured(day.featured ?? [])
    } catch {
      /* keep showing the last good data on error */
    }
  }, [sport])

  return {
    days,
    featured,
    loadingInitial,
    loadingMore,
    initialError,
    moreError,
    hasMore,
    loadMore,
    loadUntil,
    retryInitial: loadInitial,
    refetchAnchorDay,
  }
}
